import { Link } from 'react-router-dom';

import { cn } from '@/lib/cn';
import { useSector, useSectorPath } from '@/lib/sector';
import { Icon } from '@/components/icons/Icon';

/**
 * The promise, stated on every surface a buyer shops on.
 *
 * "NAFDAC verified or you don't pay" is not marketing copy that happens to sit
 * on the home screen — it is the single sentence the entire architecture exists
 * to make true. Deferred capture (§1.3), the sealed bag (§3.1), the door check
 * (§3.2) and the ungateable counterfeit refund (§9.17) are all the machinery
 * behind those six words.
 *
 * So it appears on the home screen AND on search results, because a buyer who
 * arrives from a Google search for "paracetamol Abuja" lands on results, never
 * on home, and would otherwise never be told. The point of the banner is that
 * people read it, learn what to do at the door, and then actually do it.
 *
 * Three shapes, one component:
 *
 *   `hero`     mobile / home — the full statement with the shield
 *   `compact`  search results — one line, so it informs without shouting over
 *              the results the buyer came for
 *
 * Responsive behaviour is the whole job here: at 320px the shield drops out
 * rather than squeezing the headline into four lines, at 768 the stats move
 * beside the copy, and at 1280 it becomes a true two-column hero. Nothing is
 * ever clipped and nothing ever forces the page wider than the screen.
 */

export interface TrustBannerProps {
  headline?: string;
  sub?: string;
  verifiedPacks?: number;
  counterfeits?: number;
  variant?: 'hero' | 'compact';
  className?: string;
}

const DEFAULT_HEADLINE = "NAFDAC verified or you don't pay";
const DEFAULT_SUB = 'Scan the scratch code on any pack we deliver.';

export function TrustBanner({
  headline = DEFAULT_HEADLINE,
  sub = DEFAULT_SUB,
  verifiedPacks = 0,
  counterfeits = 0,
  variant = 'hero',
  className,
}: TrustBannerProps) {
  // The promise is the same on both shelves; where it sends you is not. A
  // "Shop medicines" button on the animal-health home that lands in human
  // search is the wall failing at the one moment somebody trusted us most.
  const sector = useSector();
  const path = useSectorPath();

  if (variant === 'compact') {
    return <CompactTrustBanner verifiedPacks={verifiedPacks} className={className} />;
  }

  return (
    <section
      data-testid="trust-banner"
      aria-labelledby="trust-banner-headline"
      className={cn(
        'relative overflow-hidden rounded-card bg-[linear-gradient(135deg,var(--color-blue-deep),var(--color-blue))] text-white',
        // The padding scale is the responsive spine: comfortable at 320,
        // generous at 1280, never cramped in between.
        'p-4 sm:p-5 xl:p-8',
        className,
      )}
    >
      {/* ────────────────── THERE WAS A GHOST HERE ──────────────────
          A 280px shield outline at 6% white, bled off the bottom-right corner.
          It was meant to read as a faint watermark. What it actually read as,
          at that size and that opacity, was an angular diamond hanging behind
          the words — reported as looking like a cryptocurrency logo, which
          once seen cannot be unseen.

          Two things made it worse than a plain mistake. It sat BEHIND the
          headline, so it competed with the one sentence on this banner that
          has to be read; and on a desktop it overlapped the solid shield in
          its bordered box to the right, so the same mark appeared twice at two
          opacities, like a printing error.

          Nothing replaces it. The gradient is the decoration.
      */}

      <div className="relative flex items-start gap-3 xl:items-center xl:gap-10">
        <div className="min-w-0 flex-1">
          <span className="mb-2 inline-flex items-center gap-1.5 rounded-pill bg-white/15 px-[11px] py-[5px] text-[11.5px] font-medium">
            Every pack checked
          </span>

          {/* text-balance keeps "or you don't pay" from orphaning onto its own
              line at awkward widths. */}
          <h2
            id="trust-banner-headline"
            className="m-0 text-balance font-display text-[17px] font-bold leading-tight tracking-[-0.02em] sm:text-[21px] xl:text-[32px]"
          >
            {headline}
          </h2>

          <p className="mt-[7px] max-w-[46ch] text-[11px] leading-relaxed text-[#C3D8FF] sm:text-xs xl:text-sm">
            {sub}
          </p>

          {verifiedPacks > 0 ? (
            <p className="mt-2.5 text-[11px] text-[#C3D8FF] xl:text-[12.5px]">
              <b className="font-bold text-white">
                {verifiedPacks.toLocaleString('en-NG')} packs verified
              </b>{' '}
              this month
              {counterfeits === 0
                ? ', zero counterfeits found'
                : `, ${counterfeits} counterfeit ${counterfeits === 1 ? 'report' : 'reports'}`}
            </p>
          ) : null}

          {/* ── The two actions, side by side at every width ─────────────
              A two-column grid rather than `flex-wrap`, which stacked them on a
              phone and made the banner a tall blue column with two full-width
              bars at the bottom. Equal halves also stop the longer label
              dictating both widths.

              They are only side by side because the second label SHORTENS
              below 640px. At 320px each half is about 124px, and "How to check
              your pack" does not fit in that at any size worth reading — it
              would wrap to three lines inside its own button. */}
          <div className="mt-3.5 grid grid-cols-2 gap-2 xl:mt-5 xl:flex xl:flex-wrap">
            <Link
              to={path('/search')}
              className="inline-flex min-h-11 items-center justify-center rounded-btn bg-white px-3 text-center text-[12px] font-bold leading-tight text-blue-deep no-underline hover:bg-white/90 sm:px-4 sm:text-[13px]"
            >
              {sector === 'veterinary' ? 'Shop animal health' : 'Shop medicines'}
            </Link>
            {/* Now a real screen. NAFDAC does the verifying, not ChiCare:
                there is no QR on most Nigerian packs and no API to call, so
                /verify shows somebody how to ask NAFDAC by SMS and records
                what the reply said. */}
            <Link
              to="/verify"
              className="inline-flex min-h-11 items-center justify-center gap-1.5 rounded-btn border-[1.5px] border-white/45 px-3 text-center text-[12px] font-bold leading-tight text-white no-underline hover:bg-white/10 sm:px-4 sm:text-[13px]"
            >
              <Icon name="scan" size={15} strokeWidth={2} className="shrink-0" />
              <span className="sm:hidden">Check a pack</span>
              <span className="hidden sm:inline">How to check your pack</span>
            </Link>
          </div>
        </div>

        {/* There used to be a 200×200 box here holding a 110px shield.
            A filled shield at any size is an angular five-sided block, and
            blown up to 110px on a blue gradient it stopped reading as a shield
            at all — it read as the Ethereum logo, which is the one thing a
            Nigerian pharmacy app must not look like it is doing.

            Nothing replaces it. The headline and the gradient carry the panel,
            and an illustration that has to be explained is not earning its
            space. */}
      </div>
    </section>
  );
}

/**
 * The one-line form, for search results.
 *
 * A buyer scanning eighteen offers does not need the full pitch, but they do
 * need to know the promise exists and that checking a pack is a thing they can
 * do. One line, tappable, out of the way.
 */
function CompactTrustBanner({
  verifiedPacks,
  className,
}: {
  verifiedPacks: number;
  className?: string;
}) {
  return (
    <Link
      to="/pages/returns-policy"
      data-testid="trust-banner"
      className={cn(
        'flex min-h-11 items-center gap-2.5 rounded-card bg-[linear-gradient(135deg,var(--color-blue-deep),var(--color-blue))] px-3.5 py-2.5 text-white no-underline',
        className,
      )}
    >
      {/* min-w-0 + truncate is what stops a long sentence widening the page.
          The full statement survives at every width the app supports; only the
          supporting clause is allowed to clip. */}
      {/* The supporting line WRAPS rather than hiding below 480px. Hiding it
          left a band of empty blue on a 393px screen — the exact size §11
          designs for — and threw away the sentence that tells a buyer what to
          actually do. */}
      <span className="min-w-0 flex-1 text-[12px] leading-snug">
        <b className="block font-bold">NAFDAC verified or you don&apos;t pay.</b>
        <span className="text-[11px] text-[#C3D8FF]">
          {verifiedPacks > 0
            ? `${verifiedPacks.toLocaleString('en-NG')} packs checked this month.`
            : 'Scan the scratch code on any pack we deliver.'}
        </span>
      </span>

      <span className="hidden shrink-0 items-center gap-1 text-[11.5px] font-bold md:flex">
        How it works
      </span>
      <Icon name="chevron" size={15} strokeWidth={2.4} className="shrink-0" />
    </Link>
  );
}
